import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { Row, Col, Space, Form } from 'antd';

import React, { useEffect, useState } from 'react';
import { CustomerBusinessInfo } from './components/CustomerBusinessInfo';
import { CustomerInfo } from './components/CustomerInfo';
import { DropdownOption } from '@app/models/DropdownOption';
import { useForm } from 'antd/lib/form/Form';
import {
  createCustomer,
  editCustomer,
  viewCustomer,
  storeCustomer,
  updateCustomer,
  verifyCustomer,
} from '@app/api/customer.api';
import { notificationController } from '@app/controllers/notificationController';
import { CustomerDTO } from '@app/models/customer/CustomerDTO';
import { authUserService } from '@app/services/authUser.service';
import { useHeader } from '@app/components/header/layouts/HeaderContext';
import { useNavigate } from 'react-router-dom';

type CustomerDetailViewProps = {
  mode: 'create' | 'edit' | 'view';
  customerId?: number;
  setCurrentBreadcrumName: (name: string) => void;
};

const CustomerDetailView: React.FC<CustomerDetailViewProps> = ({ mode, customerId, setCurrentBreadcrumName }) => {
  const id = customerId ?? 0;
  const [customerForm] = useForm();
  const { addHeaderButton, removeHeaderButton } = useHeader();
  const navigate = useNavigate();
  const [isRegularCustomer, setIsRegularCustomer] = useState(false);
  const [hasRegistered, setHasRegistered] = useState(false);
  const [businessTypeOptions, setBusinessTypeOptions] = useState<DropdownOption[]>([]);
  const [companyTypeOptions, setCompanyTypeOptions] = useState<DropdownOption[]>([]);
  const [statusOptions, setStatusOptions] = useState<DropdownOption[]>([]);
  const [customerTypeOptions, setCustomerTypeOptions] = useState<DropdownOption[]>([]);
  const [hasBrOptions, setHasBrOptions] = useState<DropdownOption[]>([]);
  const [isBusiness, setIsBusiness] = useState(false);
  const isCustomer = authUserService.getUser()?.customer != null;
  const [verified, setverified] = useState(true);

  const getCustomerData = () => {
    if (mode == 'create') {
      createCustomer()
        .then((response) => {
          setApiData(response);
        })
        .catch((error) => notificationController.error({ message: error.message }));
    } else if (mode == 'edit') {
      editCustomer(id)
        .then((response) => {
          setApiData(response);
        })
        .catch((error) => notificationController.error({ message: error.message }));
    } else {
      viewCustomer(id)
        .then((response) => {
          setApiData(response);
        })
        .catch((error) => notificationController.error({ message: error.message }));
    }
  };

  const setApiData = (responseData: CustomerDTO) => {
    setCurrentBreadcrumName(responseData?.customer?.name || 'New');
    setIsRegularCustomer(responseData?.customer?.customer_type == 'REGULAR');
    setIsBusiness(responseData?.customer?.additional_info?.business_type == 'BUSINESS');
    setHasRegistered(responseData?.customer?.additional_info?.has_br == 'YES');
    setCustomerTypeOptions(responseData?.customer_type_options ?? []);
    setBusinessTypeOptions(responseData?.business_type_options ?? []);
    setCompanyTypeOptions(responseData?.company_type_options ?? []);
    setStatusOptions(responseData?.status_options ?? []);
    setHasBrOptions(responseData?.has_br_options ?? []);
    setverified(responseData?.customer?.verified);
    const companyType =
      mode == 'view'
        ? responseData?.company_type_options?.find(
            (e) => e.value == responseData?.customer?.additional_info?.company_type,
          )?.label
        : responseData?.customer?.additional_info?.company_type;

    const businessType =
      mode == 'view'
        ? responseData?.business_type_options?.find(
            (e) => e.value == responseData?.customer?.additional_info?.business_type,
          )?.label
        : responseData?.customer?.additional_info?.business_type;

    const has_br =
      mode == 'view'
        ? responseData?.has_br_options?.find((e) => e.value == responseData?.customer?.additional_info?.has_br)?.label
        : responseData?.customer?.additional_info?.has_br;

    customerForm.setFieldsValue({
      first_name: responseData?.customer?.first_name,
      email: responseData?.customer?.user?.email,
      username: responseData?.customer?.user?.username,
      last_name: responseData?.customer?.last_name,
      dob: responseData?.customer?.dob,
      contact_no: responseData?.customer?.contact_no,
      address: responseData?.customer?.address,
      nic: responseData?.customer?.nic,
      customer_type: responseData?.customer?.customer_type,
      business_type: businessType,
      status: responseData?.customer?.user?.status,
      verified: responseData?.customer.verified,
      business_name: responseData?.customer?.additional_info?.business_name,
      business_reg_no: responseData?.customer?.additional_info?.business_reg_no,
      has_br: has_br,
      business_address: responseData?.customer?.additional_info?.business_address,
      company_type: companyType,
    });
  };

  useEffect(() => {
    getCustomerData();
  }, [customerId, setCurrentBreadcrumName]);

  useEffect(() => {
    if (mode === 'edit' || mode === 'create') {
      addHeaderButton(
        'save-button',
        <BaseButton type="primary" htmlType="submit" onClick={() => customerForm.submit()} size="small">
          Save
        </BaseButton>,
      );
      addHeaderButton(
        'cancel-button',
        <BaseButton type="primary" onClick={() => navigate('/customers')} size="small">
          Cancel
        </BaseButton>,
      );
    }

    if (!verified) {
      addHeaderButton(
        'verify-button',
        <BaseButton type="primary" onClick={verify} size="small">
          Verify
        </BaseButton>,
      );
    }

    return () => {
      removeHeaderButton('save-button');
      removeHeaderButton('cancel-button');
      removeHeaderButton('verify-button');
    };
  }, [mode, verified, addHeaderButton, removeHeaderButton, customerForm, navigate]);

  const onFinish = (values: any) => {
    if (mode == 'create') {
      storeCustomer(values)
        .then((response) => {
          notificationController.success({ message: response?.message });
        })
        .catch((error) => {
          notificationController.error({ message: error?.message });
        });
    } else {
      updateCustomer(id, values)
        .then((response) => {
          notificationController.success({ message: response?.message });
        })
        .catch((error) => {
          notificationController.error({ message: error?.message });
        });
    }
  };

  const verify = () => {
    verifyCustomer(id)
      .then((response) => {
        notificationController.success({ message: response?.message });
        navigate('/new-customers');
      })
      .catch((error) => {
        notificationController.error({ message: error?.message });
      });
  };

  return (
    <BaseCard
      title="Customer"
      // extra={
      //   <Space>
      //     {!verified && (
      //       <BaseButton htmlType="button" type="primary" size="small"  onClick={()=>verify}>
      //         Verify
      //       </BaseButton>
      //     )}
      //   </Space>
      // }
    >
      <Form
        form={customerForm}
        labelCol={{ lg: { span: 6 }, md: { span: 6 }, sm: { span: 24 } }}
        labelAlign="left"
        labelWrap
        wrapperCol={{ lg: { span: 16 }, md: { span: 18 }, sm: { span: 24 } }}
        size="small"
        onFinish={onFinish}
      >
        <CustomerInfo
          mode={mode}
          statusOptions={statusOptions}
          customerTypeOptions={customerTypeOptions}
          setIsRegularCustomer={setIsRegularCustomer}
        />

        {isRegularCustomer && (
          <CustomerBusinessInfo
            hasRegistered={hasRegistered}
            isBusiness={isBusiness}
            setHasRegistered={setHasRegistered}
            setIsBusiness={setIsBusiness}
            hasBrOptions={hasBrOptions}
            businessTypeOptions={businessTypeOptions}
            companyTypeOptions={companyTypeOptions}
            mode={mode}
          />
        )}
        {/* {mode != 'view' && (
          <Row>
            <Col>
              <Space>
                <BaseButton htmlType="submit" type="primary" size="middle">
                  Save
                </BaseButton>
                <BaseButton htmlType="button" type="primary" size="middle" onClick={close}>
                  Cancel
                </BaseButton>
              </Space>
            </Col>
          </Row>
        )} */}
      </Form>
    </BaseCard>
  );
};

export default CustomerDetailView;
